// 영문 자판(qwerty) 입력을 두벌식 한글로 변환하는 유틸
// 예: 한영키를 누르지 않고 입력한 "xkdlfpshf" → "타이레놀"

const KEY_TO_JAMO: Record<string, string> = {
  q: "ㅂ", w: "ㅈ", e: "ㄷ", r: "ㄱ", t: "ㅅ",
  y: "ㅛ", u: "ㅕ", i: "ㅑ", o: "ㅐ", p: "ㅔ",
  a: "ㅁ", s: "ㄴ", d: "ㅇ", f: "ㄹ", g: "ㅎ",
  h: "ㅗ", j: "ㅓ", k: "ㅏ", l: "ㅣ",
  z: "ㅋ", x: "ㅌ", c: "ㅊ", v: "ㅍ", b: "ㅠ", n: "ㅜ", m: "ㅡ",
  // shift 입력 (쌍자음·이중모음)
  Q: "ㅃ", W: "ㅉ", E: "ㄸ", R: "ㄲ", T: "ㅆ", O: "ㅒ", P: "ㅖ",
};

const CHOSEONG = "ㄱㄲㄴㄷㄸㄹㅁㅂㅃㅅㅆㅇㅈㅉㅊㅋㅌㅍㅎ";
const JUNGSEONG = "ㅏㅐㅑㅒㅓㅔㅕㅖㅗㅘㅙㅚㅛㅜㅝㅞㅟㅠㅡㅢㅣ";
// 종성 인덱스는 1부터 (0 = 받침 없음)
const JONGSEONG = "ㄱㄲㄳㄴㄵㄶㄷㄹㄺㄻㄼㄽㄾㄿㅀㅁㅂㅄㅅㅆㅇㅈㅊㅋㅌㅍㅎ";

const VOWEL_COMPOUND: Record<string, string> = {
  "ㅗㅏ": "ㅘ", "ㅗㅐ": "ㅙ", "ㅗㅣ": "ㅚ",
  "ㅜㅓ": "ㅝ", "ㅜㅔ": "ㅞ", "ㅜㅣ": "ㅟ",
  "ㅡㅣ": "ㅢ",
};

const FINAL_COMPOUND: Record<string, string> = {
  "ㄱㅅ": "ㄳ", "ㄴㅈ": "ㄵ", "ㄴㅎ": "ㄶ",
  "ㄹㄱ": "ㄺ", "ㄹㅁ": "ㄻ", "ㄹㅂ": "ㄼ", "ㄹㅅ": "ㄽ",
  "ㄹㅌ": "ㄾ", "ㄹㅍ": "ㄿ", "ㄹㅎ": "ㅀ",
  "ㅂㅅ": "ㅄ",
};

// 겹받침 뒤에 모음이 오면 마지막 자음만 다음 음절로 넘어간다 (값+이 → 갑시)
const FINAL_SPLIT: Record<string, [string, string]> = {
  "ㄳ": ["ㄱ", "ㅅ"], "ㄵ": ["ㄴ", "ㅈ"], "ㄶ": ["ㄴ", "ㅎ"],
  "ㄺ": ["ㄹ", "ㄱ"], "ㄻ": ["ㄹ", "ㅁ"], "ㄼ": ["ㄹ", "ㅂ"], "ㄽ": ["ㄹ", "ㅅ"],
  "ㄾ": ["ㄹ", "ㅌ"], "ㄿ": ["ㄹ", "ㅍ"], "ㅀ": ["ㄹ", "ㅎ"],
  "ㅄ": ["ㅂ", "ㅅ"],
};

const isVowel = (jamo: string): boolean => JUNGSEONG.includes(jamo);

export function engToKor(input: string): string {
  const out: string[] = [];
  let cho: string | null = null;
  let jung: string | null = null;
  let jong: string | null = null;

  const flush = () => {
    if (cho && jung) {
      const jongIndex = jong ? JONGSEONG.indexOf(jong) + 1 : 0;
      out.push(
        String.fromCharCode(
          0xac00 + CHOSEONG.indexOf(cho) * 588 + JUNGSEONG.indexOf(jung) * 28 + jongIndex
        )
      );
    } else {
      if (cho) out.push(cho);
      if (jung) out.push(jung);
    }
    cho = null;
    jung = null;
    jong = null;
  };

  for (const ch of input) {
    const jamo = KEY_TO_JAMO[ch] ?? KEY_TO_JAMO[ch.toLowerCase()];

    if (!jamo) {
      flush();
      out.push(ch);
      continue;
    }

    if (isVowel(jamo)) {
      if (jong) {
        // 받침(겹받침이면 마지막 자음만)을 다음 음절 초성으로 이동
        const split = FINAL_SPLIT[jong];
        let moved: string;
        if (split) {
          jong = split[0];
          moved = split[1];
        } else {
          moved = jong;
          jong = null;
        }
        flush();
        cho = moved;
        jung = jamo;
      } else if (jung) {
        const compound = VOWEL_COMPOUND[jung + jamo];
        if (compound) {
          jung = compound;
        } else {
          flush();
          jung = jamo;
        }
      } else {
        jung = jamo;
      }
    } else {
      if (!cho && !jung) {
        cho = jamo;
      } else if (cho && !jung) {
        // 자음 연속: 앞 자음은 낱자로 확정
        flush();
        cho = jamo;
      } else if (!jong) {
        if (cho && JONGSEONG.includes(jamo)) {
          jong = jamo;
        } else {
          flush();
          cho = jamo;
        }
      } else {
        const compound = FINAL_COMPOUND[jong + jamo];
        if (compound) {
          jong = compound;
        } else {
          flush();
          cho = jamo;
        }
      }
    }
  }
  flush();

  return out.join("");
}

// 영타 오입력을 한타로 되돌릴 수 있으면 변환어를, 아니면 null을 반환한다.
// 검색 fallback 판정용: 영문 알파벳으로만 구성 + 변환 결과에 완성 음절 존재.
export function convertibleToKor(input: string): string | null {
  if (!input || !/^[a-zA-Z]+$/.test(input)) return null;
  const converted = engToKor(input);
  return /[가-힣]/.test(converted) ? converted : null;
}

// 입력 중 실시간 미리보기용: 변환 결과 전체가 완성 음절 2자 이상일 때만 변환어를 반환한다.
// 진짜 영문 검색어(tylenol, aspirin 등)는 자음군 때문에 자모 조각이 남아 걸러진다.
export function korPreview(input: string): string | null {
  const converted = convertibleToKor(input);
  return converted && converted.length >= 2 && /^[가-힣]+$/.test(converted)
    ? converted
    : null;
}
